import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getDocumentPreviewUrl } from '@/features/documents/api/get-document-preview-url';
import { useHasDocumentsHubFilters } from '@/features/documents/components/pages/documents-hub-page';
import { useDeleteDocument } from '@/features/documents/hooks/use-delete-document';
import { orgaDocumentsQueryKey } from '@/features/documents/hooks/use-documents';
import { useDownloadDocument } from '@/features/documents/hooks/use-download-document';
import {
  getProjectRoleDisplay,
  ProjectRole,
} from '@/features/projects/shared/types/project-role';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import {
  CustomTableRow,
  TableRowAction,
  TableRowProps,
} from '@/shared/components/table/table-row';
import { TableBody } from '@/shared/components/ui/table';
import {
  DocumentVisibility,
  useVisbilityFromString,
} from '@/shared/components/ui/upload/document-upload-dialog';
import { toast } from '@/shared/hooks/use-toast';
import { SecureProjectDocument } from '@/shared/types/api';

type Props = {
  documents: SecureProjectDocument[];
  onEdit: (document: SecureProjectDocument) => void;
  onAdd: () => void;
};

const DocumentsTable = ({ documents, onEdit, onAdd }: Props) => {
  const { t } = useTranslation('document', { keyPrefix: 'DocumentHub' });
  const { t: tProjectRole } = useTranslation('shared', {
    keyPrefix: 'ProjectRole',
  });
  const { t: documentT } = useTranslation('document');
  const tableColumns = [
    t('table.title'),
    t('table.description'),
    t('table.visibility'),
    t('table.project_name'),
    t('table.sub_project_name'),
    t('table.partner_name'),
    t('table.role'),
    t('table.uploaded_by'),
    t('table.date'),
  ];

  const columnWidths = [
    'w-[124px]', // Title
    'w-[136px]', // Description
    'w-[143px]', // Visibility
    'w-[188px]', // Project name
    'w-[210px]', // Sub project name
    'w-[221px]', // Partner name
    'w-[126px]', // Role
    'w-[182px]', // Uploaded by
    'flex-1', // Date
  ];

  const queryClient = useQueryClient();
  const { mutate: deleteDoc } = useDeleteDocument();
  const { mutate: downloadDoc } = useDownloadDocument();

  const { translateVisibility } = useVisbilityFromString();
  const hasFilters = useHasDocumentsHubFilters();

  const onDeleteDocument = (document: SecureProjectDocument) => {
    deleteDoc(
      { documentId: document.id, documentPath: document.file_path },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: orgaDocumentsQueryKey(document.org_id),
          });
        },
      },
    );
  };

  const onDownloadDocument = (document: SecureProjectDocument) => {
    downloadDoc(document.file_path);
  };

  const onPreviewDocument = async (document: SecureProjectDocument) => {
    try {
      const previewUrl = await getDocumentPreviewUrl(document.file_path);
      window.open(previewUrl, '_blank');
    } catch (error) {
      toast({
        title: documentT('Errors.preview_error' as any),
        description:
          error instanceof Error ? error.message : 'Failed to open preview',
      });
    }
  };

  const tableRows: TableRowProps[] = documents
    ? documents.map(
        (file): TableRowProps => ({
          type: 'single',
          onClick: () => onPreviewDocument(file),
          actions: [
            file.write_access && {
              action: <p>{t('edit')}</p>,
              onClick: () => onEdit(file),
            },
            file.write_access && {
              action: <p>{t('delete')}</p>,
              onClick: () => onDeleteDocument(file),
            },
            {
              action: <p>{t('download')}</p>,
              onClick: () => onDownloadDocument(file),
            },
          ].filter(Boolean) as TableRowAction[],
          showMenu: true,
          props: [
            {
              value: { text: file.title, type: 'string' },
              width: columnWidths[0],
            },
            {
              value: { text: file.association ?? '-', type: 'string' },
              width: columnWidths[1],
            },
            {
              value: {
                text: translateVisibility(
                  file.visibility as DocumentVisibility,
                ),
                type: 'string',
              },
              width: columnWidths[2],
            },
            {
              value: { text: file.project_name ?? '-', type: 'string' },
              width: columnWidths[3],
            },
            {
              value: { text: file.sub_project_names ?? '-', type: 'string' },
              width: columnWidths[4],
            },
            {
              value: { text: file.partner_names ?? '-', type: 'string' },
              width: columnWidths[5],
            },
            {
              value: {
                text:
                  getProjectRoleDisplay(
                    file.role as ProjectRole,
                    tProjectRole,
                  ) ?? '-',
                type: 'string',
              },
              width: columnWidths[6],
            },
            {
              value: { text: file.uploaded_by_org ?? '-', type: 'string' },
              width: columnWidths[7],
            },
            {
              value: {
                date: new Date(file.created_at),
                type: 'date',
              },
              width: columnWidths[8],
            },
          ],
        }),
      )
    : [];

  return documents.length === 0 ? (
    <EmptyTablePlaceholder
      title={
        hasFilters ? documentT('no_documents_found') : documentT('no_documents')
      }
      description={
        hasFilters ? undefined : documentT('no_documents_description')
      }
      columns={tableColumns}
      action={hasFilters ? undefined : onAdd}
      dottedBorder
    />
  ) : (
    <CustomTable>
      <CustomTableHeader columns={tableColumns} columnWidths={columnWidths} />
      <TableBody>
        {tableRows.map((row, index) => {
          return (
            <CustomTableRow
              key={index}
              props={row.props}
              type="single"
              isFirstRow={index === 0}
              isLastRow={index === tableRows.length - 1}
              actions={row.actions}
              onClick={row.onClick}
            />
          );
        })}
      </TableBody>
    </CustomTable>
  );
};

export default DocumentsTable;
